import { useEffect } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
}

/**
 * StandardsPanel — alignment statement with two authoritative 2025 reports
 * on digital twins for cities:
 *
 *   • ADB "Digital Twin Framework: A Practical Guide" (May 2025)
 *   • JTC/UNDP "Digital Twins for Cities" Urban Brief (July 2025)
 *
 * Triggered from the TopBar "DT STANDARDS" button.
 */
export function StandardsPanel({ open, onClose }: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="manual-backdrop" onClick={onClose}>
      <div
        className="manual standards-panel"
        role="dialog"
        aria-label="Digital Twin Standards Alignment"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 780, maxHeight: "85vh", overflowY: "auto" }}
      >
        <header className="manual-head">
          <div className="col">
            <span className="eyebrow mono">Standards &amp; Alignment · KCT-01</span>
            <h2 className="manual-title">Digital Twin Framework Alignment</h2>
          </div>
          <button onClick={onClose} className="mono manual-close" aria-label="Close standards panel">
            [ESC] CLOSE
          </button>
        </header>

        <div className="manual-body" style={{ padding: "1.25rem 1.5rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

          {/* Intro statement */}
          <section>
            <p style={{ lineHeight: 1.6, color: "var(--text-1)", marginBottom: "0.75rem" }}>
              The <strong>KMITL Control Tower (KCT-01)</strong> is designed and implemented in alignment
              with the frameworks and recommendations set out in two authoritative 2025 publications on
              urban digital twins. The audit below maps each framework requirement to concrete system
              capabilities — and honestly acknowledges the gaps on the current roadmap.
            </p>
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
              <ReportBadge
                org="Asian Development Bank"
                title="Digital Twin Framework: A Practical Guide"
                date="May 2025"
                color="#e05c2d"
                url="https://dx.doi.org/10.22617/SPR250158"
              />
              <ReportBadge
                org="JTC · UNDP"
                title="Digital Twins for Cities"
                date="July 2025"
                color="#1a6cb3"
                url="https://registry.sg/undp"
              />
            </div>
          </section>

          {/* ADB Maturity Level */}
          <Section title="ADB Maturity Level Assessment" color="#e05c2d">
            <p style={{ marginBottom: "0.75rem", lineHeight: 1.6 }}>
              The ADB framework defines 5 maturity levels: L1 Dynamic Representation → L2 Analytics →
              L3 Predictive → L4 Prescriptive → L5 Cognitive. KCT-01 currently operates at:
            </p>
            <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
              {[
                { l: "L1", label: "Dynamic Representation", status: "achieved" },
                { l: "L2", label: "Analytics", status: "achieved" },
                { l: "L3", label: "Predictive", status: "partial" },
                { l: "L4", label: "Prescriptive", status: "roadmap" },
                { l: "L5", label: "Cognitive", status: "roadmap" },
              ].map(({ l, label, status }) => (
                <span
                  key={l}
                  className="mono"
                  style={{
                    padding: "3px 10px",
                    borderRadius: 4,
                    fontSize: "0.7rem",
                    background:
                      status === "achieved"
                        ? "rgba(52,211,153,0.15)"
                        : status === "partial"
                          ? "rgba(251,191,36,0.15)"
                          : "rgba(113,113,122,0.15)",
                    color:
                      status === "achieved"
                        ? "var(--good)"
                        : status === "partial"
                          ? "var(--warn)"
                          : "var(--text-3)",
                    border: `1px solid ${
                      status === "achieved"
                        ? "var(--good)"
                        : status === "partial"
                          ? "var(--warn)"
                          : "var(--text-3)"
                    }`,
                  }}
                >
                  {l} · {label}
                </span>
              ))}
            </div>
            <p style={{ fontSize: "0.72rem", color: "var(--text-2)", lineHeight: 1.5 }}>
              Current score: <strong>~L2.8</strong>. Full L3 requires a connected BEMS feed with real measured
              energy data (currently synthetic). L4 (prescriptive) requires closed-loop actuation to
              BMA / MEA / MWA infrastructure — scoped for Phase 3.
            </p>
          </Section>

          {/* Gemini Principles */}
          <Section title="Gemini Principles (CDBB / ADB Appendix 10)" color="#8b5cf6">
            <p style={{ marginBottom: "0.75rem", lineHeight: 1.6, fontSize: "0.8rem" }}>
              The Centre for Digital Built Britain's nine Gemini Principles — under three pillars
              (Purpose · Trust · Function) — are the international benchmark for responsible digital
              twin governance. Each principle is mapped to KCT-01 implementation evidence:
            </p>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.72rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  <th style={{ textAlign: "left", padding: "4px 6px", color: "var(--text-2)", width: "20%" }}>Principle</th>
                  <th style={{ textAlign: "left", padding: "4px 6px", color: "var(--text-2)", width: "12%" }}>Status</th>
                  <th style={{ textAlign: "left", padding: "4px 6px", color: "var(--text-2)" }}>Evidence in KCT-01</th>
                </tr>
              </thead>
              <tbody>
                {GEMINI_ROWS.map(({ pillar, principle, status, evidence }, i) => (
                  <tr
                    key={principle}
                    style={{
                      borderBottom: "1px solid rgba(255,255,255,0.05)",
                      background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)",
                    }}
                  >
                    <td style={{ padding: "5px 6px", color: "var(--text-1)" }}>
                      <span style={{ fontSize: "0.6rem", color: "var(--text-3)", display: "block" }}>{pillar}</span>
                      {principle}
                    </td>
                    <td style={{ padding: "5px 6px" }}>
                      <StatusChip status={status} />
                    </td>
                    <td style={{ padding: "5px 6px", color: "var(--text-2)", lineHeight: 1.5 }}>{evidence}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>

          {/* ADB 5 Foundations */}
          <Section title="ADB Five Foundations of a Digital Twin" color="#e05c2d">
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {ADB_FOUNDATIONS.map(({ name, status, evidence }) => (
                <div key={name} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <StatusChip status={status} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: "0.75rem", color: "var(--text-1)" }}>{name}</span>
                    <span style={{ color: "var(--text-2)", fontSize: "0.72rem", marginLeft: 8 }}>{evidence}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* JTC/UNDP Urban Brief alignment */}
          <Section title="JTC · UNDP Urban Brief — Key Recommendations" color="#1a6cb3">
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {UNDP_RECS.map(({ rec, status, evidence }) => (
                <div key={rec} style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start" }}>
                  <StatusChip status={status} style={{ flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <span style={{ fontWeight: 600, fontSize: "0.75rem", color: "var(--text-1)" }}>{rec}</span>
                    <span style={{ color: "var(--text-2)", fontSize: "0.72rem", marginLeft: 8 }}>{evidence}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Identified gaps */}
          <Section title="Identified Gaps — Roadmap Items" color="#dc2626">
            <ul style={{ paddingLeft: "1.25rem", margin: 0, display: "flex", flexDirection: "column", gap: "0.4rem" }}>
              <GapItem
                label="Flood / climate risk simulation"
                detail="Both reports identify flood modeling as a priority use case. Bangkok's Lat Krabang district is a low-lying, flood-prone eastern zone. A storm-water runoff simulation layer is scoped for Phase 2."
              />
              <GapItem
                label="Real IoT sensor integration (BEMS)"
                detail="ADB Foundation #1 requires real-time or near-real-time data from physical sensors. Energy data is currently synthetic (EUI models). BEMS/BACnet adapter scheduled for Phase 2."
              />
              <GapItem
                label="Citizen co-creation portal"
                detail="JTC/UNDP: 'citizen participation in urban decision-making.' KCT-01 ingests Traffy Fondue citizen reports but has no resident-facing input channel or scenario-feedback loop."
              />
              <GapItem
                label="Scenario 'what-if' simulation"
                detail="ADB Maturity L3 and JTC/UNDP both emphasize 'sandbox for decision-makers to test scenarios before implementation.' Currently descriptive/analytical only."
              />
              <GapItem
                label="Formal data governance policy"
                detail="ADB Gemini Security + Curation principles require a published data governance policy (retention, access, IP). A policy document is drafted but not yet published."
              />
            </ul>
          </Section>

          {/* Footer */}
          <footer style={{ borderTop: "1px solid var(--border)", paddingTop: "0.75rem", fontSize: "0.68rem", color: "var(--text-3)", lineHeight: 1.6 }}>
            Full self-scored capability matrix: <a href="/dt-score" target="_blank" rel="noopener" style={{ color: "var(--data)" }}>/dt-score</a>
            {" · "}
            OGC SensorThings: <a href="/api/ogc/sensorthings/v1.1/" target="_blank" rel="noopener" style={{ color: "var(--data)" }}>/api/ogc/sensorthings/v1.1/</a>
            {" · "}
            NGSI-LD: <a href="/api/ngsi-ld/v1/entities" target="_blank" rel="noopener" style={{ color: "var(--data)" }}>/api/ngsi-ld/v1/entities</a>
            {" · "}
            OpenAPI: <a href="/api/docs" target="_blank" rel="noopener" style={{ color: "var(--data)" }}>/api/docs</a>
          </footer>
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ───────────────────────────────────────────────── */

function ReportBadge({ org, title, date, color, url }: { org: string; title: string; date: string; color: string; url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
        padding: "0.5rem 0.75rem",
        border: `1px solid ${color}`,
        borderRadius: 6,
        textDecoration: "none",
        flex: "1 1 280px",
        background: `${color}18`,
      }}
    >
      <span style={{ fontSize: "0.62rem", color, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>{org}</span>
      <span style={{ fontSize: "0.78rem", color: "var(--text-1)", fontWeight: 600, lineHeight: 1.3 }}>{title}</span>
      <span className="mono" style={{ fontSize: "0.64rem", color: "var(--text-3)" }}>{date}</span>
    </a>
  );
}

function Section({ title, color, children }: { title: string; color: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 style={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color, marginBottom: "0.6rem", borderBottom: `1px solid ${color}44`, paddingBottom: "0.3rem" }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

function StatusChip({ status, style }: { status: "achieved" | "partial" | "roadmap" | "gap"; style?: React.CSSProperties }) {
  const cfg = {
    achieved: { label: "✓ MET", bg: "rgba(52,211,153,0.12)", color: "var(--good)" },
    partial:  { label: "~ PARTIAL", bg: "rgba(251,191,36,0.12)", color: "var(--warn)" },
    roadmap:  { label: "→ ROADMAP", bg: "rgba(99,102,241,0.12)", color: "#818cf8" },
    gap:      { label: "✗ GAP", bg: "rgba(220,38,38,0.12)", color: "var(--bad)" },
  }[status];
  return (
    <span
      className="mono"
      style={{
        fontSize: "0.6rem",
        padding: "2px 6px",
        borderRadius: 3,
        background: cfg.bg,
        color: cfg.color,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {cfg.label}
    </span>
  );
}

function GapItem({ label, detail }: { label: string; detail: string }) {
  return (
    <li style={{ fontSize: "0.73rem", lineHeight: 1.5 }}>
      <span style={{ color: "var(--text-1)", fontWeight: 600 }}>{label}</span>
      <span style={{ color: "var(--text-2)", marginLeft: 6 }}>{detail}</span>
    </li>
  );
}

/* ── Data tables ──────────────────────────────────────────────────── */

const GEMINI_ROWS = [
  { pillar: "PURPOSE", principle: "1 · Public Good",       status: "partial" as const,  evidence: "Serves the KMITL campus + Lat Krabang district (students + the surrounding dormitory belt). No formal public-benefit assessment yet." },
  { pillar: "PURPOSE", principle: "2 · Value Creation",    status: "achieved" as const, evidence: "Energy savings proxy (15-30% anomaly detection), incident resolution, flood risk awareness. /dt-score documents value." },
  { pillar: "PURPOSE", principle: "3 · Insight",           status: "achieved" as const, evidence: "35+ live feeds → dashboards → AI executive brief. OGC SensorThings + NGSI-LD APIs for machine-readable insights." },
  { pillar: "TRUST",   principle: "4 · Security",          status: "partial" as const,  evidence: "Cloudflare Workers edge + R2 storage. No sensitive PII processed. Formal security policy not yet published." },
  { pillar: "TRUST",   principle: "5 · Openness",          status: "achieved" as const, evidence: "OpenAPI 3.1 spec at /api/docs. SensorThings + NGSI-LD open endpoints. OSM data source. Apache 2.0 codebase." },
  { pillar: "TRUST",   principle: "6 · Quality",           status: "partial" as const,  evidence: "Every feed carries provenance (source, fetchedAt, fallbackTier). Energy data clearly labeled synthetic. No formal DQAF." },
  { pillar: "FUNCTION",principle: "7 · Federation",        status: "partial" as const,  evidence: "NGSI-LD broker compatible with FIWARE Orion-LD. OGC SensorThings cross-system. Outbound push to BMA/MEA peers not wired." },
  { pillar: "FUNCTION",principle: "8 · Curation",          status: "partial" as const,  evidence: "URN scheme (urn:kct:*) + stable entity IDs. Data governance policy drafted, not published." },
  { pillar: "FUNCTION",principle: "9 · Evolution",         status: "achieved" as const, evidence: "Phased roadmap (Lite → L3 Predictive → L4 Prescriptive). Maturity score versioned at /dt-score. Designed to extend to citywide." },
];

const ADB_FOUNDATIONS = [
  { name: "F1 · Real-time data flow from physical world",   status: "partial" as const, evidence: "35+ live external feeds (weather, AQ, traffic, incidents, transit). Energy data synthetic pending BEMS." },
  { name: "F2 · Dynamic representation of physical world",  status: "achieved" as const, evidence: "deck.gl 3D + MapLibre 2D. 201 campus buildings, ARL/SRT transit, land-cover grid (20 m), real-time overlays." },
  { name: "F3 · Purpose-built for specific use cases",      status: "achieved" as const, evidence: "10 operational lenses (EXEC/OPS/MOB/ENV/SAF/VIB/UTL/EGY/AMN/X-RAY), each curated for a stakeholder role." },
  { name: "F4 · Regular updates to remain effective",       status: "partial" as const, evidence: "Live data refreshes every 30 s–60 min. Static geo refreshed via scripts. No automated OSM diff sync yet." },
  { name: "F5 · Unlocks further insights as it matures",   status: "achieved" as const, evidence: "Architecture: Lite (L1-2) operational now. L3 (BEMS), L4 (actuation) explicitly scoped. Scorecard tracks progression." },
];

const UNDP_RECS = [
  { rec: "Phased / level-by-level implementation",        status: "achieved" as const, evidence: "Explicit maturity levels (L1→L5). Campus Digital Twin Lite operational; Lat Krabang district scale next." },
  { rec: "Real-time monitoring & simulation",             status: "partial" as const,  evidence: "Real-time monitoring live. Scenario simulation (what-if) pending — no sandbox yet for planners." },
  { rec: "Energy optimization through smart grids",      status: "partial" as const,  evidence: "Per-building EUI model, anomaly detection, daily kWh. Synthetic data until BEMS integration." },
  { rec: "Flood / disaster risk modeling",               status: "roadmap" as const,  evidence: "Flood-prone areas layer exists (static). Dynamic rainfall simulation scoped for Phase 2." },
  { rec: "Transparency & stakeholder collaboration",     status: "achieved" as const, evidence: "Open API, public /dt-score, Gemini Principles declared, source catalog visible. OSM open data." },
  { rec: "Citizen participation in decision-making",     status: "roadmap" as const,  evidence: "Ingests Traffy Fondue citizen reports. No resident-facing scenario input channel yet." },
  { rec: "University / academia partnerships",           status: "achieved" as const, evidence: "Built with King Mongkut's Institute of Technology Ladkrabang. Faculty of Engineering + Architecture collaboration. Academic calendar integrated." },
  { rec: "Targeted district pilot before city-scale",   status: "achieved" as const, evidence: "Lat Krabang X-Ray demonstrates district-scale expansion from campus pilot — exactly the staged approach recommended." },
  { rec: "Low-cost open-data approach for limited budgets", status: "achieved" as const, evidence: "OSM/Overpass land cover (free, no auth), open government APIs, no proprietary satellite subscription required." },
];
